import { Component, OnInit, OnDestroy } from "@angular/core";
import {
  FormGroup,
  FormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { AuthService } from "../services/auth.service";
import { Router } from "@angular/router";
import { debounceTime, Subscription } from "rxjs";

@Component({
  selector: "app-signup",
  templateUrl: "./signup.component.html",
  styleUrl: "./signup.component.scss",
})
export class SignupComponent implements OnInit, OnDestroy {
  verifyEmailForm: FormGroup;
  signupForm: FormGroup;
  codeSent = false;
  errorMessage = "";
  isLoading = false;
  showPassword = false;
  showConfirmPassword = false;
  emailVerificationSuccessful = false;
  countdown = 5;
  countdownInterval: any;
  signUpEmail = "";
  password = "";

  signUpPending = false;
  verificationPending = false;

  private valueChangeSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService
  ) {
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.emailVerificationSuccessful = false;

    this.verifyEmailForm = this.fb.group({
      code: ["", [Validators.required, Validators.minLength(6)]],
    });

    this.signupForm = this.fb.group(
      {
        email: ["", [Validators.required, Validators.email]],
        password: [
          "",
          [
            Validators.required,
            Validators.minLength(8),
            this.containsSymbolValidator(),
            this.containsUppercaseValidator(),
          ],
        ],
        confirmPassword: [
          "",
          [Validators.required, this.passwordsMatchValidator],
        ],
      },
      {
        validators: this.passwordsMatchValidator,
      }
    );
  }

  ngOnInit(): void {
    this.valueChangeSub = this.signupForm.valueChanges
    .pipe(debounceTime(100))
    .subscribe(() => {
      if (this.errorMessage) this.errorMessage = "";
    });
  }

  ngOnDestroy(): void {
    this.valueChangeSub?.unsubscribe();
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmitSendVerificationCode() {
    this.errorMessage = "";
    this.signUpPending = true;
    this.signUpEmail = this.signupForm.get("email")?.value;
    this.password = this.signupForm.get("password")?.value;

    this.authService
      .signUpAndTriggerEmailVerification(this.signUpEmail, this.password)
      .subscribe({
        next: () => {
          this.codeSent = true;
          this.signUpPending = false;
        },
        error: (errResponse) => {
          console.log("error: ", errResponse);

          if (errResponse.error.error == "User already exists") {
            this.errorMessage =
              "<small class='error-message text-danger'>Email address provided above is already registered.</small> <br> <small> Go to the <a href='/login'>login page</a> or click <a href='/login?mode=forgot-password'>forgot password</a> to reset your password</small>";
          } else if (errResponse.error.error)
            this.errorMessage = `<small class='error-message text-danger'>${errResponse.error.error}</small>`;
          else {
            this.errorMessage = `<small class='error-message text-danger'>Failed to Sign up</small>`;
          }
          this.signupForm.valueChanges.pipe(debounceTime(100)).subscribe(() => {
            if (this.errorMessage) {
              this.errorMessage = "";
            }
          });
          this.signUpPending = false;
        },
      });
  }

  onSubmitEmailVerification() {
    this.verificationPending = true;
    const code = this.verifyEmailForm.get("code")?.value;

    this.authService.verifySignupCode(this.signUpEmail, code).subscribe({
      next: () => {
        this.verificationPending = false;
        this.emailVerificationSuccessful = true;
        this.countdownInterval = setInterval(() => {
          this.countdown--;
          if (this.countdown === 0) {
            this.router.navigate(["/login"], {
              state: { email: this.signUpEmail, password: this.password }
            });
            clearInterval(this.countdownInterval);
          }
        }, 1000);
      },
      error: () => {
        this.emailVerificationSuccessful = false;
        this.verificationPending = false;
        this.errorMessage = "Invalid verification code.";
      },
    });
  }

  passwordsMatchValidator(form: FormGroup) {
    const password = form.get("password")?.value;
    const confirmPassword = form.get("confirmPassword")?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  containsSymbolValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Return null if the control value is empty or doesn't exist
      }

      const hasSymbol = /[!@#$%^&*(),.?":{}|<>_]/.test(control.value);
      return hasSymbol ? null : { missingSymbol: true }; // Return error if no symbol
    };
  }

  containsUppercaseValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Return null if the control value is empty or doesn't exist
      }

      const hasUppercase = /[A-Z]/.test(control.value);
      return hasUppercase ? null : { missingUppercase: true }; // Return error if no uppercase letter
    };
  }
}
